import { Alert, Button, Card, DatePicker, Divider, Drawer, Form, Image, Input, Space, Switch, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { FileOutlined, FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { openNotification } from '../../utils';
import AssignmentService from './../../services/AssignmentService';
import './assignment-detail.css';

type Attachment = {
    url: string;
    mimeType: string;
};

const AssignmentDetail: React.FC<any> = ({ item, getLessonsAndAssignments }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [checked, setChecked] = useState(item.isDone);
    const [assignmentAttachment, setAssignmentAttachment] = useState<Attachment | null>(null);
    const [responseAttachment, setResponseAttachment] = useState<Attachment | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [form] = Form.useForm();
    const isExpired = Boolean(item.isExpired) && !item.isDone;

    const showModal = () => {
        setChecked(item.isDone);
        setFileList([]);
        form.setFieldsValue({
            content: item.content,
            dueDate: item.dueDate ? dayjs(item.dueDate) : undefined,
        });
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setFileList([]);
        form.resetFields();
    };

    useEffect(() => {
        if (!isModalOpen) {
            setAssignmentAttachment(null);
            setResponseAttachment(null);
            return;
        }

        let isActive = true;
        const objectUrls: string[] = [];
        const fetchAttachment = async (fileName: string, setAttachment: (attachment: Attachment) => void) => {
            try {
                const response = await AssignmentService.getFile(fileName);
                const mimeType = response.headers['content-type'] || 'application/octet-stream';
                const url = URL.createObjectURL(new Blob([response.data], { type: mimeType }));
                objectUrls.push(url);
                if (isActive) {
                    setAttachment({ url, mimeType });
                }
            } catch (error) {
                console.error('Dosya alınamadı:', error);
            }
        };

        if (item.image) {
            void fetchAttachment(item.image, setAssignmentAttachment);
        }
        if (item.responseImage) {
            void fetchAttachment(item.responseImage, setResponseAttachment);
        }

        return () => {
            isActive = false;
            objectUrls.forEach(URL.revokeObjectURL);
        };
    }, [isModalOpen, item.image, item.responseImage]);

    const onChangeDone = (newChecked: boolean) => {
        if (isExpired) {
            return;
        }

        const previousChecked = checked;
        setChecked(newChecked);
        setLoading(true);
        AssignmentService.update(item.id, { isDone: newChecked })
            .then(() => {
                openNotification('success', 'Başarılı', `Ödev durumu ${newChecked ? 'Tamamlandı' : 'Eksik'} olarak güncellendi.`);
                getLessonsAndAssignments();
            })
            .catch((error: Error) => {
                console.error(error);
                setChecked(previousChecked);
                openNotification('error', 'Başarısız', 'Ödev durumu güncellenemedi. Lütfen tekrar deneyin.');
            })
            .finally(() => setLoading(false));
    };

    const updateAssignment = () => {
        form.validateFields()
            .then((values) => {
                setLoading(true);
                const data = isExpired
                    ? { dueDate: values.dueDate.format('YYYY-MM-DDTHH:mm:ss') }
                    : {
                        content: values.content,
                        dueDate: values.dueDate.format('YYYY-MM-DDTHH:mm:ss'),
                    };
                return AssignmentService.update(item.id, data, isExpired ? undefined : fileList[0]);
            })
            .then(() => {
                openNotification('success', 'Başarılı', isExpired ? 'Teslim tarihi uzatıldı.' : 'Ödev güncellendi.');
                getLessonsAndAssignments();
                handleCancel();
            })
            .catch((error: Error) => {
                console.error(error);
                openNotification('error', 'Başarısız', 'Ödev güncellenemedi.');
            })
            .finally(() => setLoading(false));
    };

    const uploadProps: UploadProps = {
        beforeUpload: (file) => {
            setFileList([file]);
            return false;
        },
        fileList,
        maxCount: 1,
        multiple: false,
        onRemove: () => setFileList([]),
    };

    const renderAttachment = (attachment: Attachment | null, fileName: string, title: string) => {
        if (!attachment) {
            return null;
        }

        const isPdf = attachment.mimeType.includes('application/pdf') || fileName.toLowerCase().endsWith('.pdf');
        if (isPdf) {
            return (
                <>
                    <Button icon={<FilePdfOutlined />} href={attachment.url} target="_blank" rel="noreferrer">
                        PDF&apos;i Aç
                    </Button>
                    <iframe title={title} src={attachment.url} style={{ border: 0, height: 480, marginTop: 12, width: '100%' }} />
                </>
            );
        }

        const isImage = attachment.mimeType.startsWith('image/')
            || /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(fileName);
        if (isImage) {
            return <Image alt={title} src={attachment.url} fallback="error.png" />;
        }

        return (
            <Button icon={<FileOutlined />} href={attachment.url} target="_blank" rel="noreferrer">
                Dosyayı Aç / İndir
            </Button>
        );
    };

    return (
        <>
            <Button style={{ cursor: 'pointer', marginBottom: 10 }} onClick={showModal}>
                Detay
            </Button>

            <Drawer title="Ödev Detay" placement="right" onClose={handleCancel} open={isModalOpen} style={{ padding: 0, margin: 0, border: 'none' }}>
                <Card loading={loading} className="assignment-detail-card" style={{ padding: 0, margin: 0, border: 'none' }}>
                    <h4>Öğrenci Bilgileri</h4>
                    <p>{item.studentName}</p>
                    <Divider />
                    <h4>Ders</h4>
                    <p>{item.lessonName}</p>
                    <Divider />

                    <Form form={form} layout="vertical" onFinish={updateAssignment}>
                        {isExpired && <Alert message="Süresi Doldu" description="Ödevi yeniden etkinleştirmek için yalnızca teslim tarihini ileri alabilirsiniz." showIcon type="warning" />}
                        {!isExpired &&
                            <Form.Item name="content" label="Ödev İçeriği" rules={[{ required: true, message: 'Ödev içeriğini giriniz.' }]}>
                                <Input />
                            </Form.Item>
                        }
                        <Form.Item
                            name="dueDate"
                            label="Teslim Tarihi"
                            rules={[
                                { required: true, message: 'Teslim tarihini seçiniz.' },
                                {
                                    validator: (_, value) => !isExpired || value?.isAfter(dayjs())
                                        ? Promise.resolve()
                                        : Promise.reject(new Error('Teslim tarihi gelecekte olmalıdır.')),
                                },
                            ]}
                        >
                            <DatePicker className="w-full" format="DD/MM/YYYY HH:mm" showTime={{ minuteStep: 5 }} />
                        </Form.Item>
                        {!isExpired &&
                            <Form.Item label="Ödev Dosyasını Güncelle" extra="Dosyayı değiştirmek istemiyorsanız boş bırakın. Her tür dosya yükleyebilirsiniz.">
                                <Upload {...uploadProps} listType="text">
                                    <Button icon={<UploadOutlined />}>Dosya Seç</Button>
                                </Upload>
                            </Form.Item>
                        }
                        <Button htmlType="submit" type="primary" loading={loading}>
                            {isExpired ? 'Teslim Tarihini Uzat' : 'Ödevi Güncelle'}
                        </Button>
                    </Form>

                    {item.image &&
                        <>
                            <Divider />
                            <h4>Ödev Dosyası</h4>
                            {renderAttachment(assignmentAttachment, item.image, 'Ödev dosyası')}
                        </>
                    }

                    {item.responseContent &&
                        <>
                            <Divider />
                            <h4>Cevap Detayı</h4>
                            <p>{item.responseContent}</p>
                        </>
                    }
                    {item.responseImage &&
                        <>
                            <Divider />
                            <h4>Cevap Dosyası</h4>
                            {renderAttachment(responseAttachment, item.responseImage, 'Cevap dosyası')}
                        </>
                    }

                    <Divider />
                    <h4>Durum</h4>
                    <Space size="small">
                        <Switch checked={checked} disabled={loading || isExpired} onChange={onChangeDone} />
                        <span aria-live="polite">{checked ? 'Tamamlandı' : isExpired ? 'Süresi Doldu' : 'Eksik'}</span>
                    </Space>
                    <Divider />
                </Card>
            </Drawer>
        </>
    );
};

export default AssignmentDetail;
